import {getWindowWidth, isSmallScreen} from "../../blinkpay/UtilsLib";
import {MIN_SUPPORTED_WINDOW_WIDTH} from "../Constants";

export function getSandboxProperties(): string {
    return (
        "allow-popups allow-scripts allow-same-origin allow-popups-to-escape-sandbox " +
        " allow-top-navigation-by-user-activation allow-forms"
    );
}

export function getClientIdFromCurrentScriptUrl(): string | null {
    try {
        const script = document.currentScript as HTMLScriptElement | null;
        if (!script) {
            return null;
        }
        return (new URL(script.src)).searchParams.get("clientId");
    } catch {
        return null;
    }
}

export function formatAgoMessage(timestamp: number): string {
    const duration = Date.now() - timestamp;
    const msToMinute = 60 * 1000;
    const msToHour = msToMinute * 60;
    const msToDay = msToHour * 24;
    const days = parseInt((duration / msToDay).toString(), 10);
    const hours = parseInt(((duration % msToDay) / msToHour).toString(), 10);
    const minutes = parseInt(((duration % msToHour) / msToMinute).toString(), 10);
    let result = `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    if (days || hours) {
        result = `${hours} hour${hours === 1 ? "" : "s"}, ` + result;
        if (days) {
            result = `${days} day${days === 1 ? "" : "s"}, ` + result;
        }
    }
    return result;
}

export function delayThrottled(func: () => void, ms: number): () => void {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    return () => {
        if (timeoutId == null) {
            timeoutId = setTimeout(() => {
                func();
                timeoutId = null;
            }, ms);
        }
    };
}

export function delayDebounced(func: () => void, ms: number): () => void {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    return () => {
        if (timeoutId != null) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            func();
            timeoutId = null;
        }, ms);
    };
}

// Checks if the browser has support for passive event listeners.
// When an event listener is set as passive, it indicates that the callback will not cancel
// the event by invoking preventDefault().
let supportsPassiveEventsCached: boolean | null = null;
export function supportsPassiveEvents(): boolean | null {
    // Check if the variable has been previously initialized
    if (supportsPassiveEventsCached == null) {
        try {
            window.addEventListener("test", null, Object.defineProperty({}, "passive", {
                get: () => {
                    supportsPassiveEventsCached = true;
                    return null;
                }})
            );
            // eslint-disable-next-line no-empty
        } catch (e) {
        }
    }

    return supportsPassiveEventsCached;
}

export function getWidgetRightPosition(): number {
    return isSmallScreen() ? 10 : 24;
}

export function filterObjectKeys<T extends Record<string, any>>(obj: T, keys: string[]): Partial<T> {
    const newObj: Partial<T> = {};
    for (const key of Object.keys(obj)) {
        if (keys.indexOf(key) >= 0) {
            newObj[key as keyof T] = obj[key];
        }
    }
    return newObj;
}


export function computeScreenScaleFactor(): number {
    if (getWindowWidth() < MIN_SUPPORTED_WINDOW_WIDTH) {
        return (MIN_SUPPORTED_WINDOW_WIDTH / getWindowWidth());
    }
    return 1;
}


export interface OAuthCallbackArgs {
    clientId: string;
    redirectUri: string;
    scope?: string;
    state?: string;
}

// Load the OAuth params needed to login with Blink
export function loadBlinkOAuthParams(searchParams?: URLSearchParams): OAuthCallbackArgs {
    if (!searchParams) {
        searchParams = new URLSearchParams(window.location.search);
    }
    const clientId = searchParams.get("client_id");
    const redirectUri = searchParams.get("redirect_uri");
    const state = searchParams.get("state");
    const scope = searchParams.get("scope");

    if (!redirectUri) {
        throw "Missing redirectUri";
    }

    if (!clientId) {
        throw "Missing clientId";
    }

    const oauthArgs: OAuthCallbackArgs = {clientId, redirectUri};
    if (state) {
        oauthArgs.state = state;
    }
    if (scope) {
        oauthArgs.scope = scope;
    }

    return oauthArgs;
}